import React, { useEffect, useMemo, useState } from 'react';
import '../../component-styles/ReportsShared.css';
import '../../component-styles/ReportsFolder.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';

/* --------------------------------------------------------
   Helpers
-------------------------------------------------------- */
function fmtCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '-';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function fmtDateRange(start, end) {
  const toMDY = (d) => {
    if (!d) return '-';
    const [y, m, day] = d.split('-');
    if (!y || !m || !day) return d;
    return `${m}/${day}/${y}`;
  };
  return `${toMDY(start)} - ${toMDY(end)}`;
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

/* ========================================================
   PROFIT DETAIL BY PROJECT
======================================================== */
export default function ProfitDetailByProjectPage() {
  const [report, setReport] = useState(null);
  const [company, setCompany] = useState(null);
  const [allProjects, setAllProjects] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  /* Filters */
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [selectedProjectId, setSelectedProjectId] = useState('all');

  /* UI */
  const [editingDates, setEditingDates] = useState(false);

  const token = useMemo(() => localStorage.getItem('idToken'), []);
  const userId = useMemo(() => localStorage.getItem('userId'), []);

  const API = useMemo(
    () => API_BASE,
    []
  );

  /* --------------------------------------------------------
     Load Company Settings
  -------------------------------------------------------- */
  useEffect(() => {
    if (!token || !userId) return;

    (async () => {
      try {
        const res = await fetch(`${API}/api/settings/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load settings');
        const json = await res.json();
        setCompany(json?.company || null);
      } catch (err) {
        console.warn('Company settings load failed:', err);
      }
    })();
  }, [API, token, userId]);

  /* --------------------------------------------------------
     Load All Projects (for filter dropdown)
  -------------------------------------------------------- */
  useEffect(() => {
    if (!token || !userId) return;

    (async () => {
      try {
        const res = await fetch(`${API}/api/projects/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load projects');
        const json = await res.json();
        setAllProjects(Array.isArray(json) ? json : []);
      } catch (err) {
        console.warn('Project list load failed:', err);
      }
    })();
  }, [API, token, userId]);

  /* --------------------------------------------------------
     Load Detailed Report
  -------------------------------------------------------- */
  useEffect(() => {
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const qs = new URLSearchParams();
        qs.set('detailed', 'true');
        qs.set('startDate', startDate);
        qs.set('endDate', endDate);
        if (selectedProjectId !== 'all') {
          qs.set('projectId', selectedProjectId);
        }

        const res = await fetch(`${API}/api/reports/profit-by-project?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to load report');
        }

        const json = await res.json();
        setReport(json);
      } catch (err) {
        console.error('ProfitDetailByProject load failed:', err);
        setError('Failed to load profit detail report');
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token, startDate, endDate, selectedProjectId]);

  /* --------------------------------------------------------
     Computed
  -------------------------------------------------------- */
  const projects = Array.isArray(report?.projects) ? report.projects : [];

  const companyName = company?.legalName || company?.companyName || company?.name || 'Company';

  function exportExcel() {
    const headers = ['Project', 'Room', 'Income', 'Cost of Goods Sold', 'Profit'];
    const rowsForExport = [];

    projects.forEach((project) => {
      (project.rooms || []).forEach((room) => {
        rowsForExport.push([
          project.projectName || '',
          room.room || '',
          Number(room.income || 0),
          Number(room.cogs || 0),
          Number(room.profit || 0),
        ]);
      });
      rowsForExport.push([
        project.projectName || '',
        'Total',
        Number(project.totals?.income || 0),
        Number(project.totals?.cogs || 0),
        Number(project.totals?.profit || 0),
      ]);
    });

    rowsForExport.push([
      'Total',
      '',
      Number(report.grandTotals?.income || 0),
      Number(report.grandTotals?.cogs || 0),
      Number(report.grandTotals?.profit || 0),
    ]);

    exportRowsToExcel({
      filename: 'profit-detail-by-project.xlsx',
      sheetName: 'Profit Detail',
      headers,
      rows: rowsForExport,
    });
  }

  /* --------------------------------------------------------
     States
  -------------------------------------------------------- */
  if (loading)
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-card report-loading-card">
            <div className="report-loading-icon" aria-hidden="true">
              ...
            </div>
            <div className="report-loading-text">Loading profit detail…</div>
          </div>
        </div>
      </div>
    );
  if (error)
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-card report-loading-card">
            <div className="report-loading-icon" aria-hidden="true">
              !
            </div>
            <div className="report-loading-text">{error}</div>
          </div>
        </div>
      </div>
    );
  if (!report)
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-card report-loading-card">
            <div className="report-loading-icon" aria-hidden="true">
              [ ]
            </div>
            <div className="report-loading-text">No data found.</div>
          </div>
        </div>
      </div>
    );

  /* --------------------------------------------------------
     Render
  -------------------------------------------------------- */
  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-sheet">
          <header className="report-sheet-header">
            <h1>{companyName}</h1>
            <h2>Profit Detail by Project</h2>

            {/* Date Range + Pencil */}
            <div className="report-dates">
              {!editingDates ? (
                <>
                  {fmtDateRange(startDate, endDate)}
                  <button
                    className="date-edit-btn"
                    onClick={() => setEditingDates(true)}
                    aria-label="Edit date range"
                  >
                    Edit
                  </button>
                </>
              ) : (
                <div className="date-edit-inline">
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  <span>-</span>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  <button className="date-save-btn" onClick={() => setEditingDates(false)}>
                    Apply
                  </button>
                </div>
              )}
            </div>

            {/* Filter by Project */}
            <div className="report-filter-row">
              <div className="report-filter-label">Filter by</div>
              <select
                className="report-filter-select"
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
              >
                <option value="all">Project</option>
                {allProjects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>
                    {p.projectNickname}
                  </option>
                ))}
              </select>

              <button className="report-btn-secondary" onClick={exportExcel}>
                Export Excel
              </button>
            </div>
          </header>

          {/* Table */}
          <div className="financial-report-table-wrap">
            <table className="financial-report-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Income</th>
                  <th>Cost of Goods Sold</th>
                  <th>Profit</th>
                </tr>
              </thead>

              <tbody>
                {projects.map((project) => (
                  <React.Fragment key={project.projectId}>
                    {/* Project Header */}
                    <tr className="project-row">
                      <td colSpan={4}>{project.projectName}</td>
                    </tr>

                    {/* Rooms */}
                    {(project.rooms || []).map((room) => (
                      <tr key={`${project.projectId}-${room.room}`} className="room-row">
                        <td className="indent">{room.room}</td>
                        <td>{fmtCurrency(room.income)}</td>
                        <td>{fmtCurrency(room.cogs)}</td>
                        <td>{fmtCurrency(room.profit)}</td>
                      </tr>
                    ))}

                    {/* Project Total */}
                    <tr className="project-total">
                      <td>Total {project.projectName}</td>
                      <td>{fmtCurrency(project.totals.income)}</td>
                      <td>{fmtCurrency(project.totals.cogs)}</td>
                      <td>{fmtCurrency(project.totals.profit)}</td>
                    </tr>
                  </React.Fragment>
                ))}

                {/* Grand Total */}
                <tr className="grand-total">
                  <td>Total</td>
                  <td>{fmtCurrency(report.grandTotals.income)}</td>
                  <td>{fmtCurrency(report.grandTotals.cogs)}</td>
                  <td>{fmtCurrency(report.grandTotals.profit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
